import * as React from 'react';
import { Route, Switch } from 'react-router';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import * as SharedMaterialsDatabaseStateController from '../../store/StateControllers/SharedMaterials/SharedMaterialsDatabaseStateController';
import { ToxnotComponent, ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import SharedMaterialsBrowser from './SharedMaterialsBrowser';
import StartExperienceGuideComponent from '../Start/StartExperienceGuideComponent';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';

import './SharedMaterialsHome.css';

// ===============================================================================

type SharedMaterialsHomeProps =
    RouteComponentProps &
    ToxnotComponentProperties &
    SharedMaterialsDatabaseStateController.SharedMaterialsState &
    typeof SharedMaterialsDatabaseStateController.actionCreators; // ... plus action creators we've requested

class SharedMaterialsHome extends ToxnotComponent<SharedMaterialsHomeProps> {

    private readonly _RowPerPageValue: number = SharedMaterialsDatabaseStateController.rowPerPageValue;

    public componentDidMount() {
        this.props.appServices.trackCurrentUrlPath();
        this.props.appServices.trackEvent("exchange-viewed-home");
    }

    // ===============================================================================

    public componentWillMount() {
        this.props.loadExchangeFreState();
    }

    // ===============================================================================

    public render() {
        return (
            <div className="sm2-browser-root tn-section-flex-container">
                <Switch>
                    <Route exact={true} path={ToxnotReactComponentPaths.SharedMaterialsHomeStartPath}>
                        <StartExperienceGuideComponent renderAsTnSection={true} localizedHeader={this.localizeString("Get Supply Chain Data Fast")}>
                            <p>
                                {this.localizeString("Search the Passport Library, an easy way for suppliers to share their Material Passports.")}
                            </p>
                            <p>
                                <a href="http://help.exchange.3eco.com/en/articles/5717795-introducing-you-to-the-toxnot-exchange" target="_blank">{this.localizeString("Learn More about the Passport Library")}</a>
                            </p>
                            {this.props.appServices.userContext?.isEditor &&
                                <React.Fragment>
                                    <p>
                                        {this.localizeString("Not finding what you're looking for?")}
                                    </p>
                                    <p>
                                        <button type="button" className="btn btn-tn-action-complement get-supply-data-next-steps-btn" onClick={e => this.getStartedRequestNewMaterialsButton_click()}>{this.localizeString("Request New Materials")} <span className="glyphicon glyphicon-chevron-right"></span></button>
                                    </p>
                                </React.Fragment>
                            }
                        </StartExperienceGuideComponent>
                    </Route>
                </Switch>
                <div id="sm2-browser-main-section" className="tn-section">
                    <Switch>
                        <Route exact={true} path={ToxnotReactComponentPaths.BaseSharedMaterialsPath}>
                            {(this.props.hasSeenExchangeFirstRunExperience ?
                                this.renderDefaultBranding() :
                                this.renderFirstRunExperience()
                            )}
                        </Route>
                    </Switch>
                    <SharedMaterialsBrowser browserEndpoint={SharedMaterialsDatabaseStateController.BrowserEndpoint.Home} companyId={null} />
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderDefaultBranding() {
        return (
            <div id="sm2-home-branding" className="sm2-browser-header">
                <div className="content">
                    <span>Passport Library</span>
                </div>
                <div className="actions">
                    {this.renderHomeExchangeTopLevelActionButtonsLearnMore()}
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderFirstRunExperience() {
        return (
            <div id="sm2-home-fre">
                <div className="background" style={{ background: "url('/images/exchange/exchange-network-bg-graphic.svg') no-repeat, linear-gradient(to left top, #0078AE, #0F4876)" }}>
                    <div className="close-action">
                        <button type="button" className="btn btn-default exchange-fre-button" id="close-exchange-fre-button" onClick={e => this.props.setHasSeenExchangeFreState(true)}>&times;</button>
                    </div>
                    <div className="content">
                        <div className="value-prop-container">
                            <div className="value-prop">
                                <div className="text">
                                    <h1>Passport Library</h1>
                                    <h2>{this.localizeString("Shareable Product & Material Information")}</h2>
                                </div>
                            </div>
                        </div>
                        <div className="search-container">
                            <div className="search">
                                <div className="search-actions">
                                    {this.renderHomeExchangeTopLevelActionButtons()}
                                </div>
                                <div>
                                    <div className="input-group">
                                        <input id="fre-search-text-input" onKeyDown={this.freSearchInput_keyDown} type="text" className="form-control" placeholder={this.localizeString("Search by Company, Material Name, or SKU...")} />
                                        <span className="input-group-btn">
                                            <button id="fre-initiate-search-button" className="btn btn-default" type="button" onClick={e => this.runSearch()}><span className="glyphicon glyphicon-search" aria-hidden="true"></span></button>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="logos-container">
                            <div className="logos">
                                {this.renderLogo("458c89a1-f0b9-4d1f-a328-b0de349974b0", "/images/exchange/assa-abloy-logo.jpg")}
                                {this.renderLogo("bd1ca38f-f5a9-4dee-bd7c-18f76b1eb286", "/images/exchange/ecosafety-logo.png")}
                                {this.renderLogo("60810c63-50f8-46d8-aa84-bd61a916d922", "/images/exchange/autex-logo.jpg")}
                                {this.renderLogo("cde1f3de-c369-4be4-877c-6d62e4389406", "/images/exchange/nassimi-logo.png")}
                                {this.renderLogo("d6d50053-bb15-4c55-8cff-79711de4e3e0", "/images/exchange/nsg-group-logo.png")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    /* renders a logo for the FRE that links to a supplier.
     * @param supplierId the exchange ID for the supplier's company page.
     * @param logoFileLocation the file location for the image to show.
     */
    private renderLogo(supplierId: string, logoFileLocation: string) {
        return (
            <a href={"/Exchange/Database/Supplier/" + encodeURIComponent(supplierId)}>
                <div className="logo">
                    <img src={logoFileLocation} />
                </div>
            </a>
        );
    }

    // ===============================================================================

    private runSearch() {
        const searchInput = document.getElementById("fre-search-text-input") as HTMLInputElement;

        const query = searchInput.value.trim();

        if (query.length <= 0) {
            return;
        }

        this.props.loadSharedMaterialsIfNeeded(
            this.props.appServices,
            this.getQueryParameter("accessCode"),
            0,
            query,
            this._RowPerPageValue,
            {},
        );
        
        searchInput.value = "";

        this.props.setHasSeenExchangeFreState(true);
    }

    // ===============================================================================

    private renderHomeExchangeTopLevelActionButtonsBasic() {
        return (
            <React.Fragment>
                <a href={"/Data/Library"} className="btn btn-default exchange-fre-button" id="add-materials-to-exchange-button">{this.localizeString("Add your Materials")}</a>
                <Link to={ToxnotReactComponentPaths.GetStartedRequestData} className="btn btn-default exchange-fre-button" id="invite-suppliers-to-exchange-button">{this.localizeString("Invite your Suppliers")}</Link>
            </React.Fragment>
        );
    }

    // ===============================================================================

    private renderHomeExchangeTopLevelActionButtons() {
        return (
            <div>
                {this.renderHomeExchangeTopLevelActionButtonsBasic()}
            </div>
        );
    }

    // ===============================================================================

    private renderHomeExchangeTopLevelActionButtonsLearnMore() {
        return (
            <div>
                {this.renderHomeExchangeTopLevelActionButtonsBasic()}
                <button type="button" className="btn btn-default exchange-fre-button" id="show-exchange-fre-button" onClick={e => this.props.setHasSeenExchangeFreState(false)}>{this.localizeString("Learn More")}</button>
            </div>
        );
    }

    // ===============================================================================

    //
    // UI Events
    //

    private freSearchInput_keyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            this.runSearch();
        }
    }

    // ===============================================================================

    private getStartedRequestNewMaterialsButton_click = () => {
        this.props.appServices.trackEvent("get-started-request-new-materials-clicked");

        this.props.history.push("/Start/InviteSuppliers");
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ ...state.sharedMaterialsState, appServices: state.appServices }),
    SharedMaterialsDatabaseStateController.actionCreators // Selects which action creators are merged into the component's props
)(withRouter(SharedMaterialsHome as any));
